import { ApplicationConfig, Provider, provideBrowserGlobalErrorListeners, provideZoneChangeDetection, provideEnvironmentInitializer, inject } from '@angular/core';
import { COMPOSITION_BUFFER_MODE } from '@angular/forms';
import { provideRouter, withHashLocation, withRouterConfig, withInMemoryScrolling, RouteReuseStrategy } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { importProvidersFrom } from '@angular/core';
import { provideNgxWebstorage, withSessionStorage, withLocalStorage } from 'ngx-webstorage';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { definePreset } from '@primeng/themes';
import { DialogService } from 'primeng/dynamicdialog';

import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptor,
  MsalModule,
  MsalService,
} from '@azure/msal-angular';

import { routes } from './app.routes';
import { errorHandlerInterceptor } from './blocks/interceptor/error-handler.interceptor';
import { authExpiredInterceptor } from './blocks/interceptor/auth-expired.interceptor';
import { networkLogInterceptor } from './core/diagnostics/network-log.interceptor';
import { DiagnosticsBufferService } from './core/diagnostics/diagnostics-buffer.service';
import { CustomTranslateLoader } from './app.custom-translate-loader';
import { MSALInstanceFactory, MSALInterceptorConfigFactory, MSALGuardConfigFactory } from './core/auth/msal-config';
import { CandidateAwareMsalInterceptor } from './core/auth/candidate-aware-msal.interceptor';
import { initFontAwesome } from './core/icons/fontawesome-icons';
import { AppRouteReuseStrategy } from './core/routing/app-route-reuse-strategy';

declare var leanbyte: any;

export function createTranslateLoader(http: HttpClient): CustomTranslateLoader {
  return new CustomTranslateLoader(http);
}

function getMsalProviders(): Provider[] {
  return [
    // KCS-565 Part 2: the candidate-aware wrapper is the registered interceptor; it delegates to a plain
    // (non-multi) MsalInterceptor for staff and forwards candidate requests untouched.
    MsalInterceptor,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CandidateAwareMsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService
  ];
}

/*
 * PrimeNG v21 DynamicDialog defaults closable to false (unlike other props
 * that use the `!== false` pattern). Patch DialogService.open() once so every
 * DynamicDialog gets a close button unless explicitly opted out.
 */
const _origOpen = DialogService.prototype.open;
DialogService.prototype.open = function (componentType: any, config: any) {
  return _origOpen.call(this, componentType, { closable: true, ...config });
};

const isMicrosoftLogin = typeof leanbyte !== 'undefined' && leanbyte.microsoftLogin === true;

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withHashLocation(),
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
    ),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: definePreset(Aura, {
          semantic: {
            primary: {
              50: '#E8EFFE',
              100: '#BBD1FD',
              200: '#99B9FC',
              300: '#6698FA',
              400: '#3B78F9',
              500: '#0F5EF8',
              600: '#0D4FD4',
              700: '#0A42B0',
              800: '#053BA5',
              900: '#032D7D',
              950: '#021437',
            },
          },
        }),
        options: {
          darkModeSelector: '.app-dark',
        },
      },
      // Overlays ohne Ein-/Ausblenden: PrimeNG bindet den Klick-nach-draussen-Lauscher
      // und raeumt das Panel erst am Ende der Bewegung auf. Wird die Bewegung
      // unterbrochen - zwei Auswahlfelder schnell hintereinander -, bleibt das Panel
      // im DOM haengen und der naechste Klick scheint nichts zu tun. Ohne Bewegung
      // laufen die Rueckmeldungen sofort und der Zustand bleibt richtig.
      overlayOptions: {
        motionOptions: { disabled: true },
      },
      // Der Popover fragt die Bewegungsoptionen nur ueber pt ab, nicht ueber
      // overlayOptions. Ohne das hier braucht ein Wechsel von einer Zelle zur
      // naechsten drei Klicks: schliessen, nichts, oeffnen.
      pt: {
        popover: { motion: { disabled: true } },
      },
    }),
    provideHttpClient(
      withInterceptors([errorHandlerInterceptor, authExpiredInterceptor, networkLogInterceptor]),
      withInterceptorsFromDi()
    ),
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'de',
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient]
        }
      }),
      FontAwesomeModule,
    ),
    provideNgxWebstorage(withSessionStorage(), withLocalStorage()),
    provideEnvironmentInitializer(() => {
      const library = inject(FaIconLibrary);
      initFontAwesome(library);
    }),
    // Instantiate the diagnostics buffer early so its console wrappers are installed at
    // startup (feeds the cable-harness "Report a Bug" flow with console + network logs).
    provideEnvironmentInitializer(() => {
      inject(DiagnosticsBufferService);
    }),
    { provide: RouteReuseStrategy, useClass: AppRouteReuseStrategy },
    // Suchfelder sollen beim Tippen filtern, auch auf dem Tablet. Angular haelt den
    // Wert sonst bis zum Ende eines Wortes zurueck (Composition) und entscheidet das
    // allein an der Kennung des Browsers: steht dort kein "android <Ziffer>", bleibt
    // das Puffern an. Ein Android-Tablet mit "Desktopseite anfordern" meldet genau das
    // nicht, tippt aber mit GBoard weiter wortweise - die Eingabe erreichte das Modell
    // dann erst beim Leerzeichen, waehrend (input) schon mit dem alten Text suchte.
    // Auf dem iPad faellt es nicht auf: Safari meldet beim normalen Tippen gar keine
    // Composition. Betrifft jedes Feld mit [(ngModel)] + (input), nicht nur die Matrix.
    { provide: COMPOSITION_BUFFER_MODE, useValue: false },
    // Root-level DialogService so app-wide services like iOS share-fallback 
    // dialog can open DynamicDialogs. Components that declare their own
    // `providers: [DialogService]` still get their more-specific instance unchanged.
    DialogService,
    ...(isMicrosoftLogin ? getMsalProviders() : [])
  ]
};
